import { ClickAwayListener, useMediaQuery } from '@mui/material'
import { Card } from '@mui/material'
import { useTour } from '@reactour/tour'
import { AppUIType, useAppUIType } from 'components/providers/AppUITypeProvider'
import { useAuth } from 'components/providers/AuthProvider'
import { useCalendar } from 'components/providers/CalendarProvider'

import { joinGroupIfNeeded } from 'components/providers/GetChangesProvider'
import { useIcloud } from 'components/providers/icloud/IcloudProvider'
import TFMEventsView from 'components/tfmDateSortedItems/TFMEventsView'
import dayjs from 'dayjs'
import ThirdPartyCalendarAccount from 'model/models/ThirdPartyCalendarAccount'
import { ReactNode, useEffect, useState } from 'react'
import { DateRange } from 'react-day-picker'
import Database from 'services/db/Database'
import { Account } from 'services/db/Queries/AccountQueries'
import { RemoveTempEvents } from 'services/db/Queries/Event'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import { ThirdPartyAccountByEmail } from 'services/db/Queries/ThirdPartyCalendarsQuieries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { DocType } from 'types/api/changes'
import { CalendarRange } from 'types/components/calendar'
import { ConnectedGoogleCalendar } from 'types/google_events'
import { CollectionType } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { isUserActiveInGroup } from 'utils/api/groups'
import { fetchCalendarsFromGoogle } from 'utils/google_events'
import { isGuest } from 'utils/user'

import CalendarHeader from './CalendarHeader'
import CalendarView from './CalendarView'
import * as Styled from './styled'
import { DateRangePicker } from './ViewDatesPicker'
import { ProviderType } from 'utils/groups'

export interface CalendarProps {
  readOnly?: boolean
  defaultView?: CalendarRange
  showOnlySelectedGroup?: boolean
  groupId?: string
  dayNameFormat?: string
  groupIds?: string[]
  calendarTitle?: string
  embedMode?: boolean
}

export default function Calendar(props: CalendarProps) {
  const calendar = useCalendar()
  const auth = useAuth()
  const appUItype = useAppUIType()
  const icloud = useIcloud()
  const { isOpen, currentStep, steps, setIsOpen, setCurrentStep, setSteps } =
    useTour()

  function dateRangeSelected(selected: Date | DateRange | undefined): void {
    Logger.blue('selected', selected)
    setTimeout(() => {
      if (selected instanceof Date) {
        calendar.onGoToDate(selected as Date)
      } else if (selected) {
        calendar.onGoToDate((selected as DateRange).from ?? new Date())
      }
    }, 1)
  }

  function joinGroupFromCache() {
    /**
     * join pending group here, to update screen after join
     */
    if (auth.firstGetChangesFinished) {
      try {
        joinGroupIfNeeded().then((group) => {
          if (group) {
            Logger.debug('joined group', group)
            calendar.onGroupServer(group._id ?? '')
          }
        })
      } catch (e) {
        Logger.red('join', e)
      }
    }
  }

  useEffect(() => {
    localGroupById(LocalStorage.get(LocalStorageKey.CACHED_GROUP_ID_JOIN) ?? '')
      .then((group) => {
        //group found, need to check if user active there, if so, perform group selection

        if (group) {
          if (
            isUserActiveInGroup(
              group,
              LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
            )
          ) {
            calendar.onGroupServer(group._id)

            LocalStorage.remove(LocalStorageKey.CACHED_GROUP_ID_JOIN)
            LocalStorage.remove(LocalStorageKey.CACHED_GROUP_PASSWORD)
          } else {
            joinGroupFromCache()
          }
        }
      })
      .catch((e) => {
        console.error(e)
        //group not found, or error during check if user are part of a group
        try {
          joinGroupFromCache()
        } catch (e) {
          console.error(e)
        }
      })

    Account().then((account) => {
      /**
       * We collect all the connected accounts by email keys
       */

      const connectedEmails = Object.keys(
        account?.ConnectedCalAccounts ?? {}
      ).filter((item) => item.includes('@'))

      connectedEmails.forEach(async (key) => {
        await ThirdPartyAccountByEmail(key, ProviderType.GOOGLE).then(
          (thirdPartyAcc) => {
            Logger.blue('Account:', thirdPartyAcc)
            if (!thirdPartyAcc) {
              // in case it's not exist already
              if ((account.ConnectedCalAccounts[key] as any).Google) {
                Logger.blue('Account:Creating Google Calendar', key)
                fetchCalendarsFromGoogle(
                  key,
                  (account.ConnectedCalAccounts[key] as any).Google
                ).catch((error) => {
                  const connectedGoogleCalendar: ConnectedGoogleCalendar = (
                    account.ConnectedCalAccounts[key] as any
                  ).Google
                  Logger.pink(
                    'creating calendar with issue',
                    connectedGoogleCalendar
                  )
                })
              }
            }
          }
        )

        await ThirdPartyAccountByEmail(key, ProviderType.APPLE).then((acc) => {
          if (!acc) {
            if ((account.ConnectedCalAccounts[key] as any).iCloud) {
              ThirdPartyAccountByEmail(key, ProviderType.APPLE)
                .then((account) => {
                  Logger.blue('Account:iCloud', account)
                  return Database.write(async () => {
                    return account
                      ? account.update((local) => {})
                      : Database.getCollection<ThirdPartyCalendarAccount>(
                          CollectionType.GoogleAccount
                        ).create((local) => {
                          local.provider = ProviderType.APPLE
                          local.email = key
                        })
                  })
                })
                .then((thirdPartyAccount) => {
                  console.log('thirdPartyAccount', thirdPartyAccount)
                  icloud.sync()
                })
            }
          }
        })
      })
    })

    //clear popup state on very first load

    LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)

    setIsOpen(true)
    console.log('isOpen', isOpen, steps)
  }, [auth.firstGetChangesFinished])

  let content: ReactNode = <></>

  // we need this hook in order to refresh event data on a screen based on a visible area
  const [localStorageValue, setLocalStorageValue] = useState(
    LocalStorage.get(LocalStorageKey.VISIBLE_FROM)
  )
  useEffect(() => {
    function handleStorageChange() {
      setTimeout(() => {
        setLocalStorageValue(LocalStorage.get(LocalStorageKey.VISIBLE_FROM))
      }, 1)
    }

    window.addEventListener('storage', handleStorageChange)

    // Cleanup function
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  switch (appUItype.uiType.value) {
    case AppUIType.calendars:
      content = (
        <ClickAwayListener
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
          onClickAway={() => {
            calendar.onGroupInfo(undefined)
            if (LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) != '1') {
              RemoveTempEvents()
              LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
            }
          }}
        >
          <>
            <CalendarHeader
              title={props.calendarTitle}
              groupID={calendar.groupServerID}
              currentView={isGuest() ? CalendarRange.WEEK : props.defaultView}
              embedMode={props.embedMode}
              dateRangeSelected={dateRangeSelected}
            />

            <div
              style={{
                position: 'relative',
                height: '100%'
              }}
            >
              <CalendarView
                embedMode={props.embedMode}
                dayNameFormat={props.dayNameFormat}
                defaultView={props.defaultView}
                readOnly={props.readOnly}
                groupID={props.groupId ?? calendar.groupServerID}
                showOnlySelectedGroup={props.showOnlySelectedGroup}
                visibleFrom={dayjs(
                  LocalStorage.get(LocalStorageKey.VISIBLE_FROM)
                )
                  .subtract(1, 'day')
                  .unix()}
                visibleTo={dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_TO))
                  .add(1, 'day')
                  .unix()}
                groupIds={props.groupIds}
              />
            </div>
          </>
        </ClickAwayListener>
      )
      break
    case AppUIType.notes:
      return <TFMEventsView type={DocType.NOTE} />

    case AppUIType.tasks:
      return <TFMEventsView type={DocType.TASK} />
  }

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'row',
        boxShadow: `0px 4px ${
          props.readOnly ? '15px' : '30px'
        } rgba(0, 0, 0, 0.3) !important`,
        gap: 0,
        '@media (max-height: 500px)': {
          padding:
            calendar.view === CalendarRange.LISTDAY ||
            calendar.view === CalendarRange.LISTWEEK
              ? 4
              : 0,
          borderRadius: 4,
          display: 'flex',
          flexDirection: 'row',
          gap: 4
        }
      }}
    >
      <DateRangeForSmallScreens
        readOnly={props.readOnly}
        dateRangeSelected={dateRangeSelected}
      />
      <Styled.Calendar
        sx={{
          boxShadow: `0px 4px ${
            props.readOnly ? '15px' : '30px'
          } rgba(0, 0, 0, 0.3) !important`,
          width: '100%',
          height: '100%',
          flexGrow: 1
        }}
        padding={false}
      >
        {content}
      </Styled.Calendar>
    </Card>
  )
}

function DateRangeForSmallScreens({
  readOnly,
  dateRangeSelected
}: {
  readOnly?: boolean
  dateRangeSelected: (selected: Date | DateRange | undefined) => void
}) {
  const calendar = useCalendar()
  const visibleAreaStart = dayjs(calendar.fromISO)
  const visibleAreaEnd = dayjs(calendar.toISO).subtract(1, 'day')
  const isHeightBelowCondesed = useMediaQuery('(max-height: 500px)')

  if (
    calendar.view !== CalendarRange.LISTDAY &&
    calendar.view !== CalendarRange.LISTWEEK
  ) {
    return <></>
  }

  return (
    <Card
      sx={{
        display: 'none',
        borderRadius: 4,
        overflowY: 'auto',

        height: 'fit-content',
        boxShadow: `0px 4px ${
          readOnly ? '15px' : '30px'
        } rgba(0, 0, 0, 0.3) !important`,
        '@media (max-height: 500px)': {
          display: 'block'
        }
      }}
    >
      {isHeightBelowCondesed && (
        <DateRangePicker
          mode={calendar.view === CalendarRange.LISTWEEK ? 'range' : 'single'}
          defaultRange={{
            from: visibleAreaStart.toDate(),
            to: visibleAreaEnd.toDate()
          }}
          defaultDate={calendar.scrollToDate.toDate()}
          onSelect={dateRangeSelected}
        />
      )}
    </Card>
  )
}
